/**
 * The astronomy engine — the mathematical heart of the application.
 *
 * This module is STRICTLY UI-independent: it knows nothing about screens,
 * sensors, or rendering. It converts:
 *
 *   DATE/TIME + OBSERVER LAT/LON + CELESTIAL COORDINATES
 *     -> observer-relative horizontal coordinates (alt/az)
 *
 * Coordinate frame conventions (see types.ts):
 *
 *   World frame (horizontal, "HOR"):  x = north, y = west, z = zenith.
 *   Equatorial of-date ("EQD"):       x = toward the equinox of date,
 *                                     z = toward the north celestial pole.
 *   Equatorial J2000 ("EQJ"):         same axes, but the J2000 equinox.
 *
 * Conversion pipeline (rotations performed by astronomy-engine's validated
 * NOVAS-derived rotation matrices):
 *
 *   Stars:        v_hor = R_EQD_HOR(time, obs) * R_EQJ_EQD(time) * v_j2000
 *   Solar system: v_hor = R_EQD_HOR(time, obs) * v_equator_of_date
 *
 *   altitude = asin(v_hor.z)             [-90, +90]
 *   azimuth  = atan2(-v_hor.y, v_hor.x)  [0, 360), clockwise from north
 *
 * Precision policy:
 *   - Star positions: catalog-derived (J2000), accuracy limited by catalog
 *     epoch and proper motion — typically < 1 arcminute for naked-eye stars
 *     over a century.
 *   - Solar-system positions: high precision (VSOP87-class theory inside
 *     astronomy-engine), well under an arcminute for the sky-map use case.
 *   - Refraction: standard Bennett model when enabled.
 */

import {
  Body,
  Equator,
  Horizon,
  InverseRefraction,
  MakeTime,
  Observer,
  Refraction,
  Rotation_EQD_HOR,
  Rotation_EQJ_EQD,
  Rotation_HOR_EQD,
  RotateVector,
  SearchHourAngle,
  SearchRiseSet,
  SphereFromVector,
  Vector,
  type AstroTime,
  type RotationMatrix,
} from "astronomy-engine";

import type {
  CelestialCoordinate,
  HorizontalCoordinate,
  ObserverLocation,
  ObserverTime,
  SolarSystemBody,
} from "./types";
import { Planet } from "./types";

const DEG2RAD = Math.PI / 180;
const RAD2DEG = 180 / Math.PI;
const HOURS2RAD = Math.PI / 12;

function clamp(value: number, lo: number, hi: number): number {
  return value < lo ? lo : value > hi ? hi : value;
}

function toAeBody(body: SolarSystemBody): Body {
  switch (body) {
    case "Sun":
      return Body.Sun;
    case "Moon":
      return Body.Moon;
    case Planet.Mercury:
      return Body.Mercury;
    case Planet.Venus:
      return Body.Venus;
    case Planet.Mars:
      return Body.Mars;
    case Planet.Jupiter:
      return Body.Jupiter;
    case Planet.Saturn:
      return Body.Saturn;
    case Planet.Uranus:
      return Body.Uranus;
    case Planet.Neptune:
      return Body.Neptune;
  }
}

function toObserver(observer: ObserverLocation): Observer {
  return new Observer(observer.latitudeDegrees, observer.longitudeDegrees, observer.elevationMeters ?? 0);
}

function toAstroTime(time: ObserverTime): AstroTime {
  return MakeTime(new Date(time.utcMilliseconds));
}

/** Unit vector (x = East, y = North, z = Up) from altitude/azimuth. */
export function vectorFromHorizontal(altitudeDegrees: number, azimuthDegrees: number) {
  const alt = altitudeDegrees * DEG2RAD;
  const az = azimuthDegrees * DEG2RAD;
  const cosAlt = Math.cos(alt);
  return { x: cosAlt * Math.sin(az), y: cosAlt * Math.cos(az), z: Math.sin(alt) };
}

/**
 * Horizontal coordinates from a unit vector in the HOR frame
 * (x = north, y = west, z = zenith).
 *   altitude = asin(z)
 *   azimuth  = atan2(-y, x) normalized to [0, 360) — clockwise from north.
 */
export function horizontalFromVector(v: Readonly<{ x: number; y: number; z: number }>): HorizontalCoordinate {
  const altitudeDegrees = Math.asin(clamp(v.z, -1, 1)) * RAD2DEG;
  let azimuthDegrees = Math.atan2(-v.y, v.x) * RAD2DEG;
  if (azimuthDegrees < 0) azimuthDegrees += 360;
  return { altitudeDegrees, azimuthDegrees };
}

/** Unit vector in an equatorial frame (same construction for J2000 and of-date). */
function vectorFromEquatorial(rightAscensionHours: number, declinationDegrees: number) {
  const dec = declinationDegrees * DEG2RAD;
  const ra = rightAscensionHours * HOURS2RAD;
  const cosDec = Math.cos(dec);
  return { x: cosDec * Math.cos(ra), y: cosDec * Math.sin(ra), z: Math.sin(dec) };
}

/**
 * Converts a celestial coordinate to observer horizontal coordinates.
 *
 * @param coordinate J2000 or of-date RA/Dec.
 * @param refraction  true to apply the standard atmospheric refraction model.
 */
export function equatorialToHorizontal(
  observer: ObserverLocation,
  time: ObserverTime,
  coordinate: CelestialCoordinate,
  refraction = true,
): HorizontalCoordinate {
  const t = toAstroTime(time);
  const obs = toObserver(observer);
  let vEqd: { x: number; y: number; z: number };
  if (coordinate.epoch === "OF_DATE") {
    vEqd = vectorFromEquatorial(coordinate.rightAscensionHours, coordinate.declinationDegrees);
  } else {
    const vJ2000 = vectorFromEquatorial(coordinate.rightAscensionHours, coordinate.declinationDegrees);
    vEqd = RotateVector(Rotation_EQJ_EQD(t), new Vector(vJ2000.x, vJ2000.y, vJ2000.z, t));
  }
  const vHor = RotateVector(Rotation_EQD_HOR(t, obs), new Vector(vEqd.x, vEqd.y, vEqd.z, t));
  const h = horizontalFromVector(vHor);
  if (refraction && h.altitudeDegrees > -1) {
    return { ...h, altitudeDegrees: applyRefraction(h.altitudeDegrees) };
  }
  return h;
}

/** Standard Bennett refraction (degrees of lift), via astronomy-engine. */
export function applyRefraction(altitudeDegrees: number): number {
  return altitudeDegrees + Refraction("normal", altitudeDegrees);
}

/** Removes standard refraction from an apparent altitude. */
export function removeRefraction(altitudeDegrees: number): number {
  return InverseRefraction("normal", altitudeDegrees);
}

/**
 * Inverse of equatorialToHorizontal (refraction-free): horizontal -> of-date
 * equatorial coordinate. Used by tap-to-identify.
 *
 * The library's horizontal frame is (x = north, y = west, z = zenith), while
 * vectorFromHorizontal produces (x = east, y = north, z = up); the two are
 * related by v_horLib = (v_enu.y, -v_enu.x, v_enu.z).
 */
export function horizontalToEquatorialOfDate(
  observer: ObserverLocation,
  time: ObserverTime,
  horizontal: HorizontalCoordinate,
): CelestialCoordinate {
  const t = toAstroTime(time);
  const obs = toObserver(observer);
  const vEnu = vectorFromHorizontal(horizontal.altitudeDegrees, horizontal.azimuthDegrees);
  const vHorLib = new Vector(vEnu.y, -vEnu.x, vEnu.z, t);
  const vEqd = RotateVector(Rotation_HOR_EQD(t, obs), vHorLib);
  const eqd = SphereFromVector(vEqd);
  return {
    rightAscensionHours: eqd.lon / 15,
    declinationDegrees: eqd.lat,
    epoch: "OF_DATE",
  };
}

/**
 * Active rotation matrix mapping J2000 equatorial vectors to the of-date
 * (precessed) mean equator/equinox frame, for the given instant. `rot` is
 * row-major 3x3; applying it: v_ofdate = rot * v_j2000 (column vector).
 * This is the same rotation the engine applies internally for J2000
 * coordinates in equatorialToHorizontal.
 */
export function j2000ToOfDateRotation(time: ObserverTime): RotationMatrix {
  return Rotation_EQJ_EQD(toAstroTime(time));
}

/**
 * Inverse of j2000ToOfDateRotation: maps of-date equatorial vectors back to
 * J2000. Used by tap-to-identify to compare a tapped direction (an of-date
 * coordinate) against catalog objects stored in J2000.
 */
export function ofDateToJ2000Rotation(time: ObserverTime): RotationMatrix {
  const rot = Rotation_EQJ_EQD(toAstroTime(time)).rot;
  return {
    rot: [
      [rot[0]![0]!, rot[1]![0]!, rot[2]![0]!],
      [rot[0]![1]!, rot[1]![1]!, rot[2]![1]!],
      [rot[0]![2]!, rot[1]![2]!, rot[2]![2]!],
    ],
  };
}

export interface RiseSetTransitTimes {
  /** Time the body's center crosses the horizon (rise), UTC, or null if never. */
  rise: Date | null;
  /** Time the body's center crosses the horizon (set), UTC, or null if never. */
  set: Date | null;
  /** Time the body crosses the local meridian (upper culmination), UTC. */
  transit: Date | null;
}

export class SkyEngine {
  /** Alt/az of a catalog (J2000) star for a given observer and time. */
  starToHorizontal(
    observer: ObserverLocation,
    time: ObserverTime,
    rightAscensionHours: number,
    declinationDegrees: number,
    refraction = true,
  ): HorizontalCoordinate {
    return equatorialToHorizontal(
      observer,
      time,
      { rightAscensionHours, declinationDegrees, epoch: "J2000" },
      refraction,
    );
  }

  /** Topocentric of-date equatorial coordinate of a solar-system body. */
  bodyCoordinate(
    body: SolarSystemBody,
    observer: ObserverLocation,
    time: ObserverTime,
    aberration = true,
  ): CelestialCoordinate {
    const eq = Equator(toAeBody(body), toAstroTime(time), toObserver(observer), true, aberration);
    return { rightAscensionHours: eq.ra, declinationDegrees: eq.dec, epoch: "OF_DATE" };
  }

  /** Alt/az of a solar-system body for a given observer and time. */
  bodyToHorizontal(
    body: SolarSystemBody,
    observer: ObserverLocation,
    time: ObserverTime,
    refraction = true,
  ): HorizontalCoordinate {
    const eqd = this.bodyCoordinate(body, observer, time, true);
    return equatorialToHorizontal(observer, time, eqd, refraction);
  }

  /**
   * Rise/set/transit for a solar-system body on the given date.
   * Rise/set use the standard -0.833 deg horizon (refraction + solar radius).
   * Returns null entries for circumpolar bodies (never rise/set).
   */
  riseSetTransit(
    body: SolarSystemBody,
    observer: ObserverLocation,
    time: ObserverTime,
  ): RiseSetTransitTimes {
    const obs = toObserver(observer);
    const start = toAstroTime(time);
    let rise: Date | null = null;
    let set: Date | null = null;
    try {
      const riseTime = SearchRiseSet(toAeBody(body), obs, 1, start, 2);
      rise = riseTime ? new Date(riseTime.date) : null;
      const setTime = riseTime
        ? SearchRiseSet(toAeBody(body), obs, -1, riseTime, 2)
        : SearchRiseSet(toAeBody(body), obs, -1, start, 2);
      set = setTime ? new Date(setTime.date) : null;
    } catch {
      // Body never rises/sets at this observer (circumpolar case).
    }
    let transit: Date | null = null;
    try {
      const transitTime = SearchHourAngle(toAeBody(body), obs, 0, start);
      transit = transitTime ? new Date(transitTime.time.date) : null;
    } catch {
      // Ignore: some bodies have no transit within the search window.
    }
    return { rise, set, transit };
  }
}

/** Singletons. */
export const skyEngine = new SkyEngine();
export { Body, Horizon };
